import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';

import { clientDetailQueryOptions } from '~/entities/client';
import {
  CLIPS_PER_EXERCISE,
  clipOfClient,
  clipQueryOptions,
  clipTimelineQueryKey,
  clipTimelineQueryOptions,
  type ClipEntry,
  type ClipPerson,
} from '~/entities/exercise-clip';
import { useExerciseNames } from '~/entities/exercise-library';
import { useLiftHistory } from '~/entities/lift-history';
import { useSessionUser } from '~/entities/session';
import { isGone } from '~/shared/api/mutator';
import { displayInitials } from '~/shared/lib/displayInitials';

import { buildPlayerView, type PlayerView } from '../model/playerView';

import { useClipDiscussion, type ClipDiscussion } from './useClipDiscussion';
import { useClipActivity, useClipSeen } from './useClipSeen';

export interface ClipPlayerData {
  /**
   * The movement's recordings, or the lift history that says which set each one is, are not known
   * yet. The list and the set block both wait for both, so no label changes under the trainer.
   */
  readonly pending: boolean;
  readonly failed: boolean;
  /** The clip no longer exists, or is not this client's. Nothing is played, marked or written. */
  readonly gone: boolean;
  readonly retry: () => void;
  /** The film would not load: read the clip again and put its freshly signed urls in the list. */
  readonly reloadFilm: (clipId: number) => Promise<void>;
  readonly view: PlayerView;
  readonly clientName: string;
  readonly clientInitials: string;
  readonly clientAvatarUrl: string | null;
  readonly exerciseName: string;
  readonly capacity: { readonly count: number; readonly max: number } | null;
  readonly discussion: ClipDiscussion;
  readonly viewer: ClipPerson;
  /** A frame of this clip was presented: record that the trainer saw it, when that is due. */
  readonly markSeen: (clipId: number) => void;
}

const NO_CLIPS: readonly ClipEntry[] = [];

/**
 * @param onActivity the trainer saw or answered a clip: the page refreshes what it lists from it.
 * @param onSeenGaveUp the seen mark was refused on every attempt; called once per opening.
 */
export function useClipPlayerData(
  clientId: number,
  clipId: number,
  onActivity: () => void,
  onSeenGaveUp: () => void,
): ClipPlayerData {
  const { t, i18n } = useTranslation();
  const queryClient = useQueryClient();
  const user = useSessionUser();
  const [now] = useState(Date.now);

  const clip = useQuery({
    ...clipQueryOptions(clipId),
    retry: (count, error) => !isGone(error) && count < 1,
  });
  const mine = clipOfClient(clip.data, clientId);
  const refused = (clip.isSuccess && mine === null) || (clip.isError && isGone(clip.error));
  // Kept across a switch to another recording of the movement, so the list and the film stay on
  // screen while the new clip's own read is in flight.
  const [movement, setMovement] = useState<number | null>(null);
  if (mine !== null && mine.exerciseId !== movement) setMovement(mine.exerciseId);
  const exerciseId = refused ? null : (mine?.exerciseId ?? movement);
  const timeline = useQuery({
    ...clipTimelineQueryOptions(clientId, exerciseId ?? 0),
    enabled: exerciseId !== null,
  });
  const header = useQuery(clientDetailQueryOptions(clientId));
  const names = useExerciseNames(exerciseId === null ? [] : [exerciseId]);
  const history = useLiftHistory(clientId, exerciseId);

  const refreshActivity = useClipActivity(onActivity);

  const viewer: ClipPerson = {
    userId: user?.id ?? null,
    fullName: user?.displayName ?? '',
    initials: displayInitials(user?.displayName ?? ''),
    avatarUrl: user?.avatarUrl ?? null,
  };
  const discussion = useClipDiscussion(
    clipId,
    mine !== null,
    exerciseId === null ? null : { clientId, exerciseId },
    viewer,
    refreshActivity,
  );

  const clips = exerciseId === null ? NO_CLIPS : (timeline.data ?? NO_CLIPS);
  const known = clips.some((entry) => entry.id === clipId);
  const withCurrent = known || mine === null ? clips : [...clips, mine];
  const listPending =
    exerciseId === null ? clip.isPending || (clip.isError && clip.isFetching) : timeline.isPending;

  const markSeen = useClipSeen(clientId, refreshActivity, onSeenGaveUp);

  return {
    pending: listPending || (exerciseId !== null && history.pending),
    failed: (clip.isError && !refused) || timeline.isError,
    gone: refused,
    retry: () => {
      void clip.refetch();
      if (exerciseId !== null) void timeline.refetch();
    },
    reloadFilm: async (id) => {
      const fresh = await queryClient
        .fetchQuery({ ...clipQueryOptions(id), staleTime: 0 })
        .catch(() => null);
      if (fresh === null) return;
      queryClient.setQueryData<readonly ClipEntry[]>(
        clipTimelineQueryKey(clientId, fresh.exerciseId),
        (entries) => entries?.map((entry) => (entry.id === id ? { ...entry, media: fresh.media } : entry)),
      );
    },
    view: buildPlayerView(
      {
        clips: withCurrent,
        clipId,
        sessions: history.sessions,
        comments: discussion.comments,
        viewerUserId: viewer.userId,
      },
      { t, locale: i18n.language, now },
    ),
    clientName: header.data?.person?.fullName ?? '',
    clientInitials: header.data?.person?.initials ?? '',
    clientAvatarUrl: header.data?.person?.avatarUrl ?? null,
    exerciseName: exerciseId === null ? '' : (names.names.get(exerciseId) ?? ''),
    capacity:
      timeline.data === undefined || exerciseId === null
        ? null
        : { count: clips.length, max: Math.max(clips.length, CLIPS_PER_EXERCISE) },
    discussion,
    viewer,
    markSeen: (id) => {
      if (exerciseId !== null && mine !== null) markSeen(id, exerciseId);
    },
  };
}
